import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Bell,
  Search,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeft,
  ChevronRight,
  Command,
  X,
  Clock,
  MessageSquare,
  Package,
  ShoppingCart,
  CheckCircle2,
  AlertCircle,
  Info,
  LogOut,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Notification types with icons and colors
const notificationTypes = {
  order: {
    icon: ShoppingCart,
    color: "text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-900/30",
  },
  message: {
    icon: MessageSquare,
    color: "text-purple-500",
    bg: "bg-purple-50 dark:bg-purple-900/30",
  },
  success: {
    icon: CheckCircle2,
    color: "text-green-500",
    bg: "bg-green-50 dark:bg-green-900/30",
  },
  warning: {
    icon: AlertCircle,
    color: "text-amber-500",
    bg: "bg-amber-50 dark:bg-amber-900/30",
  },
  info: {
    icon: Info,
    color: "text-cyan-500",
    bg: "bg-cyan-50 dark:bg-cyan-900/30",
  },
  inventory: {
    icon: Package,
    color: "text-indigo-500",
    bg: "bg-indigo-50 dark:bg-indigo-900/30",
  },
};

// Sample notifications
const sampleNotifications = [
  {
    id: 1,
    type: "order",
    title: "New Order Received",
    message: "Order #1234 has been placed",
    time: "2 min ago",
    unread: true,
  },
  {
    id: 2,
    type: "inventory",
    title: "Low Stock Alert",
    message: "Product SKU-789 is running low",
    time: "15 min ago",
    unread: true,
  },
  {
    id: 3,
    type: "success",
    title: "Payment Received",
    message: "Invoice #5678 has been paid",
    time: "1 hour ago",
    unread: false,
  },
  {
    id: 4,
    type: "message",
    title: "New Message",
    message: "You have a new inquiry from customer",
    time: "3 hours ago",
    unread: false,
  },
];

// Breadcrumb mapping
const breadcrumbLabels = {
  dashboard: "Dashboard",
  customers: "Customers",
  orders: "Orders",
  invoices: "Invoices",
  crm: "CRM",
  products: "Products",
  add: "Add New",
  edit: "Edit",
  inventory: "Inventory",
  categories: "Categories",
  suppliers: "Suppliers",
  reports: "Reports",
  "inventory-reports": "Inventory Reports",
  users: "User Management",
  "company-setup": "Company Setup",
  "company-administration": "Company Admin",
  delivery: "Delivery Order",
};

export default function TopBar({ onSidebarOpen, isSidebarOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef(null);
  const notificationRef = useRef(null);

  // Quick actions for search
  const quickActions = [
    { label: "Go to Dashboard", path: "/admin/dashboard", icon: "🏠" },
    { label: "Manage Customers", path: "/admin/customers", icon: "👥" },
    { label: "User Management", path: "/admin/users", icon: "👤" },
    { label: "Manage Products", path: "/admin/products", icon: "🛍️" },
    { label: "Add New Product", path: "/admin/products/add", icon: "➕" },
    { label: "Manage Categories", path: "/admin/categories", icon: "📁" },
    { label: "Check Inventory", path: "/admin/inventory", icon: "📊" },
    { label: "Add Inventory", path: "/admin/inventory/add", icon: "📥" },
    { label: "Manage Suppliers", path: "/admin/suppliers", icon: "🏭" },
    { label: "View Orders", path: "/admin/orders", icon: "📦" },
    { label: "Delivery Orders", path: "/admin/delivery-orders", icon: "🚚" },
    { label: "View Invoices", path: "/admin/invoices", icon: "🧾" },
    { label: "View Reports", path: "/admin/reports", icon: "📈" },
    { label: "CRM", path: "/admin/crm", icon: "💼" },
    {
      label: "Inventory Reports",
      path: "/admin/inventory-reports",
      icon: "📋",
    },
    { label: "Company Setup", path: "/admin/company-setup", icon: "⚙️" },
    {
      label: "Company Administration",
      path: "/admin/company-administration",
      icon: "🏢",
    },
  ];

  // Filter quick actions based on search query
  const filteredActions = searchQuery.trim()
    ? quickActions.filter((action) =>
        action.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : quickActions;

  // Reset selected index when search query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return (
      savedTheme === "dark" ||
      (!savedTheme && window.matchMedia("(prefers-color-scheme: dark)").matches)
    );
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
        setSelectedIndex(0);
        setTimeout(() => searchRef.current?.focus(), 100);
      }
      if (e.key === "Escape") {
        setShowSearch(false);
        setShowNotifications(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Keyboard navigation for search modal
  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < filteredActions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredActions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const action = filteredActions[selectedIndex];
      if (action) {
        navigate(action.path);
        setShowSearch(false);
        setSearchQuery("");
        setSelectedIndex(0);
      }
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/notifications/list?limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(
          data.data.map((n) => ({
            id: n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            time: new Date(n.createdAt).toLocaleString(),
            unread: !n.read,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter((n) => n.unread).length;

  // Generate breadcrumbs from path
  const generateBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [];

    paths.forEach((path, index) => {
      if (path === "admin") return;
      const label =
        breadcrumbLabels[path] || path.charAt(0).toUpperCase() + path.slice(1);
      const fullPath = "/" + paths.slice(0, index + 1).join("/");
      breadcrumbs.push({
        label,
        path: fullPath,
        isLast: index === paths.length - 1,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/notifications/mark-all-read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  return (
    <>
      {/* Main Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50 transition-colors duration-200">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {/* Sidebar Toggle */}
            <button
              onClick={onSidebarOpen}
              className="p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
            >
              {isSidebarOpen ? (
                <PanelLeftClose className="h-5 w-5" />
              ) : (
                <PanelLeft className="h-5 w-5" />
              )}
            </button>

            {/* Breadcrumbs - Hidden on mobile */}
            <nav className="hidden md:flex items-center text-sm">
              <span className="text-gray-400 dark:text-gray-500">Home</span>
              {breadcrumbs.map((crumb, index) => (
                <div key={crumb.path} className="flex items-center">
                  <ChevronRight className="w-4 h-4 mx-2 text-gray-300 dark:text-gray-600" />
                  <span
                    className={`${
                      crumb.isLast
                        ? "text-gray-900 dark:text-white font-medium"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
                    }`}
                    onClick={() => !crumb.isLast && navigate(crumb.path)}
                  >
                    {crumb.label}
                  </span>
                </div>
              ))}
            </nav>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Search Button */}
            <button
              onClick={() => setShowSearch(true)}
              className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
            >
              <Search className="h-4 w-4" />
              <span className="hidden lg:inline">Quick search...</span>
              <kbd className="hidden lg:flex items-center gap-0.5 px-2 py-0.5 text-xs font-medium text-gray-400 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                <Command className="w-3 h-3" />
                <span>K</span>
              </kbd>
            </button>

            {/* Mobile Search */}
            <button
              onClick={() => setShowSearch(true)}
              className="sm:hidden p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                      {unreadCount}
                    </span>
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Notifications
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {unreadCount} unread messages
                        </p>
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      {notifications.map((notification) => {
                        const TypeIcon =
                          notificationTypes[notification.type]?.icon || Info;
                        const typeColor =
                          notificationTypes[notification.type]?.color ||
                          "text-gray-500";
                        const typeBg =
                          notificationTypes[notification.type]?.bg ||
                          "bg-gray-50";

                        return (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer ${
                              notification.unread
                                ? "bg-blue-50/50 dark:bg-blue-900/10"
                                : ""
                            }`}
                          >
                            <div className="flex gap-3">
                              <div
                                className={`flex-shrink-0 w-10 h-10 ${typeBg} rounded-xl flex items-center justify-center`}
                              >
                                <TypeIcon className={`w-5 h-5 ${typeColor}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {notification.title}
                                  </p>
                                  {notification.unread && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                                  {notification.message}
                                </p>
                                <div className="flex items-center gap-1 mt-1.5">
                                  <Clock className="w-3 h-3 text-gray-400" />
                                  <span className="text-[11px] text-gray-400">
                                    {notification.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <button
                        onClick={() => {
                          navigate("/admin/notifications");
                          setShowNotifications(false);
                        }}
                        className="w-full py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      >
                        View all notifications
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200"
              title={
                isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"
              }
            >
              <motion.div
                initial={false}
                animate={{ rotate: isDarkMode ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </motion.div>
            </button>

            {/* Logout Button */}
            <button
              onClick={logout}
              className="p-2.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all duration-200"
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      <AnimatePresence>
        {showSearch && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50"
              onClick={() => setShowSearch(false)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="w-full max-w-2xl pointer-events-auto"
              >
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="flex items-center gap-3 px-4 border-b border-gray-200 dark:border-gray-800">
                    <Search className="w-5 h-5 text-gray-400" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Search for anything..."
                      className="flex-1 py-4 text-sm text-gray-900 dark:text-white bg-transparent border-0 focus:outline-none focus:ring-0 placeholder-gray-400"
                      autoFocus
                    />
                    <kbd className="hidden sm:flex items-center px-2 py-1 text-xs font-medium text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-md">
                      ESC
                    </kbd>
                    <button
                      onClick={() => setShowSearch(false)}
                      className="sm:hidden p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-2 max-h-96 overflow-y-auto">
                    {/* Quick Actions */}
                    <div className="px-3 py-2">
                      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        {searchQuery.trim()
                          ? "Search Results"
                          : "Quick Actions"}
                      </p>
                    </div>

                    {filteredActions.length > 0 ? (
                      filteredActions.map((action, index) => (
                        <button
                          key={action.path}
                          onClick={() => {
                            navigate(action.path);
                            setShowSearch(false);
                            setSearchQuery("");
                            setSelectedIndex(0);
                          }}
                          onMouseEnter={() => setSelectedIndex(index)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${
                            selectedIndex === index
                              ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          <span className="text-lg">{action.icon}</span>
                          <span className="flex-1 text-left">
                            {action.label}
                          </span>
                          {selectedIndex === index && (
                            <kbd className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 rounded">
                              ↵
                            </kbd>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                        No results found for "{searchQuery}"
                      </div>
                    )}
                  </div>

                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Type to search across all modules</span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          ↑
                        </kbd>
                        <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          ↓
                        </kbd>
                        <span>to navigate</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          ↵
                        </kbd>
                        <span>to select</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
